import React, { useState, useMemo, useEffect } from 'react';
import { openDillToast } from '../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  updateApplicationUpdateHistory,
  updateSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { DillButton, DillDropdown, DillModal } from '../../../general/Dill';
import {
  CreditApplication,
  matchBallInCourtRoute,
  parseBallInCourtAmount,
} from '@dill/dill-shared';

const ReadyForDecisionModal = ({
  open,
  handleClose,
  creditApplication,
  applicationId,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplication: (CreditApplication & { applicationHistoryId: string | null }) | null;
  applicationId: string;
}) => {
  const dispatch = useAppDispatch();
  const { user, users } = useAppSelector((globalState) => globalState.auth);
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  const [loading, setLoading] = useState(false);
  const [errorReviewerId, setErrorReviewerId] = useState('');
  const [reviewerId, setReviewerId] = useState('');

  // Same rules the backend applies (the form's own when it has any) — see matchBallInCourtRoute.
  const autoSelectedReviewerId = useMemo(
    () =>
      matchBallInCourtRoute({
        routes: currentSupplier?.creditApplicationSetting?.ballInCourtRouting,
        status: 'READY_FOR_DECISION',
        templateId: creditApplication?.templateId,
        branchId: creditApplication?.branchId,
        readAmount: (fieldId) =>
          parseBallInCourtAmount(
            creditApplication?.fieldValues?.find((field) => field.id === fieldId)?.value
          ),
      }) ?? '',
    [currentSupplier?.creditApplicationSetting?.ballInCourtRouting, creditApplication]
  );

  useEffect(() => {
    if (open) {
      setReviewerId(autoSelectedReviewerId);
      setErrorReviewerId('');
    }
  }, [open, autoSelectedReviewerId]);

  const handleCloseModal = () => {
    setLoading(false);
    setReviewerId('');
    setErrorReviewerId('');
    handleClose();
  };

  const handleShare = async () => {
    if (!reviewerId.trim()) {
      setErrorReviewerId('Reviewer is required.');
      return;
    }
    try {
      setLoading(true);
      let response = null;
      if (!creditApplication?.applicationHistoryId) {
        response = await dispatch(
          updateSupplierCreditApplication({
            creditApplicationId: applicationId,
            supplierId: currentSupplier?.id || '',
            status: 'READY_FOR_DECISION',
            assignedReviewerUserId: reviewerId,
          })
        );
      } else {
        response = await dispatch(
          updateApplicationUpdateHistory({
            applicationHistoryId: creditApplication?.applicationHistoryId ?? '',
            creditApplicationId: applicationId,
            supplierId: currentSupplier?.id || '',
            status: 'READY_FOR_DECISION',
            assignedReviewerUserId: reviewerId,
          })
        );
      }

      if (response && response.type.includes('fulfilled')) {
        openDillToast({ message: 'Status successfully updated!', type: 'SUCCESS' });
        handleCloseModal();
      } else {
        openDillToast({ message: 'Failed to update status!', type: 'ERROR' });
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message: error?.message || 'Failed to share application.',
      });
    } finally {
      setLoading(false);
    }
  };

  const items = (users ?? []).map((usr) => ({
    label: usr?.firstName + ' ' + usr?.lastName,
    value: usr?.id,
  }));

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Ready for Decision"
      subtitle="Select a reviewer to assign for the final decision."
      width="480px"
      footer={
        <div className="flex justify-end gap-2">
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Submit"
            variant="primary"
            onClick={handleShare}
            isLoading={loading}
            isDisabled={loading}
          />
        </div>
      }>
      <div className="flex flex-col gap-4 py-2">
        <DillDropdown
          label="Reviewer"
          isRequired
          errorText={errorReviewerId}
          value={reviewerId}
          items={items}
          isSearchable
          placeholder="Select a reviewer..."
          onSelectChange={(item) => {
            if (item?.value) {
              setReviewerId(item.value);
              setErrorReviewerId('');
            }
          }}
          containerClassName={'h-[36px]'}
        />
      </div>
    </DillModal>
  );
};

export default ReadyForDecisionModal;
